import { useState } from "react";
import { Link } from "react-router-dom";
import FileAttachments from "./FileAttachments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { openAuthedFile } from "@/lib/files";
import { useVacLibrary, selectedVacCodes, candidateVacCodes, frDate } from "@/lib/vac";
import { BookMarked, Printer, Eye, Plus, AlertTriangle, X } from "lucide-react";

// Cartes VAC du briefing : proposées d'après la route, depuis la bibliothèque (dernière édition).
export default function VacSelection({ briefing, setBriefing, briefingId, onExportVac, exporting }) {
  const { byCode } = useVacLibrary();
  const [add, setAdd] = useState("");
  const selected = selectedVacCodes(briefing);
  const candidates = candidateVacCodes(briefing);

  const toggle = (code, on) =>
    setBriefing((b) => {
      const ex = new Set(b.vacExcluded || []);
      on ? ex.delete(code) : ex.add(code);
      return { ...b, vacExcluded: [...ex] };
    });
  const addCode = () => {
    const c = add.trim().toUpperCase();
    if (!/^[A-Z0-9]{4}$/.test(c)) return;
    setBriefing((b) => ({ ...b, vacAdded: [...new Set([...(b.vacAdded || []), c])], vacExcluded: (b.vacExcluded || []).filter((x) => x !== c) }));
    setAdd("");
  };
  const removeAdded = (c) => setBriefing((b) => ({ ...b, vacAdded: (b.vacAdded || []).filter((x) => x !== c) }));

  return (
    <div className="glass rounded-2xl p-6" data-testid="vac-selection">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">
          <BookMarked className="h-4 w-4" /> Cartes VAC du briefing
        </h3>
        {selected.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => onExportVac(selected)} disabled={exporting} data-testid="vac-export-2up-button"
            className="border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Imprimer les VAC (2 par A4)
          </Button>
        )}
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Proposées d'après la route (départ, dégagements, arrivée). Cochez celles à utiliser ; elles proviennent de votre <Link to="/vac" className="text-cyan-400 hover:underline">bibliothèque Cartes VAC</Link>.
      </p>
      <div className="space-y-3">
        {candidates.map((code) => {
          const chart = byCode[code];
          const on = selected.includes(code);
          return (
            <div key={code} data-testid={`vac-terrain-${code}`} className={`rounded-xl border p-3 ${on ? "border-cyan-500/30 bg-cyan-500/5" : "border-slate-800 bg-slate-900/40 opacity-70"}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Checkbox checked={on} onCheckedChange={(v) => toggle(code, !!v)} data-testid={`vac-select-${code}`} />
                <span className="font-mono-data text-base font-bold text-cyan-300">{code}</span>
                {chart ? (
                  <>
                    <span className="text-xs text-slate-300" data-testid={`vac-status-${code}`}>{chart.name ? `${chart.name} · ` : ""}édition du {frDate(chart.edition_date)}</span>
                    <button onClick={() => openAuthedFile(`/vac-charts/${chart.id}/download`)} data-testid={`vac-open-${code}`}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-cyan-300 hover:bg-cyan-500/10"><Eye className="h-3.5 w-3.5" /> Voir</button>
                  </>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-amber-300" data-testid={`vac-status-${code}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Absente de la bibliothèque — <Link to="/vac" className="underline">l'ajouter</Link>
                  </span>
                )}
                {(briefing.vacAdded || []).includes(code) && (
                  <button onClick={() => removeAdded(code)} className="ml-auto rounded p-1 text-slate-500 hover:text-rose-300" data-testid={`vac-remove-added-${code}`}><X className="h-4 w-4" /></button>
                )}
              </div>
              {on && <div className="mt-2"><FileAttachments briefingId={briefingId} pointId={`vac::${code}`} title="Autre fichier VAC propre à ce briefing (optionnel)" compact /></div>}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Input value={add} maxLength={4} onChange={(e) => setAdd(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && addCode()}
          placeholder="Autre terrain (OACI)" className="h-9 w-48 border-slate-700 bg-slate-900/60 font-mono-data text-sm text-slate-100" data-testid="vac-add-code-input" />
        <Button size="sm" variant="outline" onClick={addCode} data-testid="vac-add-code-button" className="border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
          <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter
        </Button>
      </div>
    </div>
  );
}
